import { DeleteOutlined, InboxOutlined, MobileOutlined } from '@ant-design/icons';
import { AppConsts, L } from '@lib';
import { Button, Spin, Typography } from 'antd';
import QRCode from 'react-qr-code';
import React from 'react';

const { Text } = Typography;

interface UploadedFile {
  base64: string;
  name: string;
  contentType: string;
}

interface IProps {
  uploadedFile: UploadedFile | null;
  parsing: boolean;
  captureToken: string | null;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  onParse: () => void;
}

export const InvoiceFileUpload: React.FC<IProps> = ({
  uploadedFile,
  parsing,
  captureToken,
  onFileSelect,
  onRemove,
  onParse,
}) => {
  const openFileDialog = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) onFileSelect(file);
    };
    input.click();
  };

  if (!uploadedFile) {
    return (
      <>
        <div
          onClick={openFileDialog}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file) onFileSelect(file);
          }}
          style={{
            border: '2px dashed #d9d9d9',
            borderRadius: 8,
            padding: '24px 0',
            textAlign: 'center',
            cursor: 'pointer',
            background: '#fafafa',
            transition: 'border-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#2CBDFF')}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#d9d9d9')}
        >
          <p style={{ marginBottom: 4 }}>
            <InboxOutlined style={{ fontSize: 28, color: '#2CBDFF' }} />
          </p>
          <p style={{ margin: 0, fontSize: 14 }}>
            {L('Click or drag invoice file to upload')}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8c8c8c' }}>
            {L('JPG, PNG, PDF')}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
          <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>OR</Text>
          <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            padding: '12px 16px',
            background: '#fafafa',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
          }}
        >
          {captureToken ? (
            <div style={{ background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #e8e8e8', flexShrink: 0 }}>
              <QRCode
                value={`${AppConsts.appBaseUrl}/capture/${captureToken}`}
                size={100}
                level="M"
              />
            </div>
          ) : (
            <Spin size="small" />
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <MobileOutlined style={{ fontSize: 16, color: '#2CBDFF' }} />
              <Text strong>{L('Scan with your phone')}</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {L('Scan the QR code to take a photo from your phone. The image will appear here automatically.')}
            </Text>
          </div>
        </div>
      </>
    );
  }

  return (
    <div style={{ marginBottom: 16, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <InboxOutlined style={{ fontSize: 18, color: '#2CBDFF', flexShrink: 0 }} />
          <Text strong ellipsis style={{ fontSize: 13 }}>{uploadedFile.name}</Text>
        </div>
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={onRemove}>
          {L('Remove')}
        </Button>
      </div>

      {uploadedFile.contentType.startsWith('image/') && uploadedFile.base64 && (
        <div style={{ padding: 12, display: 'flex', justifyContent: 'center', background: '#fff' }}>
          <img
            src={uploadedFile.base64}
            alt="Invoice preview"
            style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 4, border: '1px solid #e8e8e8' }}
          />
        </div>
      )}

      <div style={{ padding: '12px 14px', background: '#fafafa', display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={onParse}
          type="primary"
          loading={parsing}
        >
          {L('Extract Data with AI')}
        </Button>
      </div>
    </div>
  );
};
